import { useQuery } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { useMemo } from "react";
import { useParams } from "react-router-dom";

import { ccfApiClient } from "../api/api-clients";

export type IdentityOrganisation = {
  id: string;
  name: string;
  sandbox: boolean;
  services: string[];
};

export type HttpException = {
  statusCode: number;
  message: string;
};

/**
 * The organisation of the customer route being viewed, or why it cannot be used. Outside customer
 * routes it is `emptyOrganisation`.
 */
export type OrganisationContext = IdentityOrganisation | "unusable-organisation" | "non-existent-organisation";

export const emptyOrganisation: OrganisationContext = {
  id: "",
  name: "",
  sandbox: false,
  services: [],
};

export type UseOrganisationResult = {
  isLoading: boolean;
  data: AxiosResponse<IdentityOrganisation> | undefined;
  isError: boolean;
  error: HttpException | null;
};

/** Exported so tests can seed the cache instead of mocking. */
export const organisationQueryKey = (customerId?: string) => ["getOrganisation", customerId] as const;

const fetchOrganisation = async (customerId?: string): Promise<AxiosResponse<IdentityOrganisation>> => {
  if (!customerId) {
    throw new Error("Customer ID is undefined");
  }
  return ccfApiClient.get<IdentityOrganisation>(`/identity/organisation/${customerId}`);
};

export const useOrganisation = (customerId?: string): UseOrganisationResult => {
  const { isLoading, data, isError, error } = useQuery<AxiosResponse<IdentityOrganisation>, HttpException>({
    queryKey: organisationQueryKey(customerId),
    queryFn: () => fetchOrganisation(customerId),
    retry: false,
    enabled: Boolean(customerId),
  });

  return { isLoading, data, isError, error };
};

/**
 * The header renders above `OrganisationAccessGuard`, so this only observes the guard's query
 * (`enabled: false`): it never requests on its own, which would race the auth token.
 */
export const useOrganisationName = (customerId?: string): string | undefined => {
  const { data } = useQuery<AxiosResponse<IdentityOrganisation>, HttpException>({
    queryKey: organisationQueryKey(customerId),
    queryFn: () => fetchOrganisation(customerId),
    enabled: false,
  });

  return data?.data.name;
};

/**
 * 404 means the identity service does not know the organisation; any other failure leaves it unusable.
 * `undefined` while a customer route's organisation has been requested but not answered yet.
 */
export const toOrganisationContext = (
  customerId: string | undefined,
  data: AxiosResponse<IdentityOrganisation> | undefined,
  error: HttpException | null
): OrganisationContext | undefined => {
  if (!customerId) {
    return emptyOrganisation;
  }
  if (error) {
    return error.statusCode === 404 ? "non-existent-organisation" : "unusable-organisation";
  }
  if (!data) {
    return undefined;
  }
  const { id, name, sandbox, services } = data.data;
  return { id, name, sandbox, services };
};

/**
 * The organisation of the current customer route once the identity service has answered, `undefined`
 * until then. For code that renders above `OrganisationAccessGuard` (the monitoring provider) and must
 * not act on the empty placeholder while the fetch is in flight.
 */
export const useSettledOrganisationContext = (): OrganisationContext | undefined => {
  const { customerId } = useParams();
  const { data, error } = useOrganisation(customerId);

  return useMemo(() => toOrganisationContext(customerId, data, error), [customerId, data, error]);
};

/**
 * The organisation of the current customer route, read from the same query `OrganisationAccessGuard`
 * settles before it renders anything, so below the guard the value is final on the first render.
 */
export const useOrganisationContext = (): OrganisationContext => useSettledOrganisationContext() ?? emptyOrganisation;
